// __CARGO_V2__ (2026-10-27) the hub's cargo list on the hauling board: CATEGORY -> TYPE.
// Henry: "make it a few different drop-downs - categories. Keep it simple: about 4 categories, about 10 items each."
// The hub owns the list (Settings -> General -> Cargo types); this site reads it with the signed-in person's own
// token from GET hub.hmgc.io/api/cargo-types (the hub answers this site cross-origin for exactly that GET), keeps
// the last good copy in this browser, and never edits it. A job keeps its cargo as the TYPE name in loads.commodity;
// an old name ("Skidsteer", "Fabricated Steel") still shows, under the category of the type it maps to (`aliases`).
// The pure half (key / itemOf / categoryOf / stopMinutes) is the twin of the hub's cargo_types.py.
import { supabase } from '../supabaseClient';
import { HUB } from './hubDrafts';

const CACHE_KEY = 'hm_cargo_vocab_v2';
const TTL_MS = 30 * 60 * 1000;
let cur = null;            // { groups: [{name, items}], aliases: {old: item}, at }
let inflight = null;

/** Matching key: case, spaces and punctuation ignored; & = and, + = plus (cargo_types.key). */
export const cargoKey = (t) => String(t || '').toLowerCase().replace(/&/g, ' and ').replace(/\+/g, ' plus ').replace(/[^a-z0-9]+/g, '');

const stripParen = (t) => String(t || '').replace(/\s*\([^)]*\)\s*$/, '').trim();

export const cleanVocab = (j) => {
  const groups = (Array.isArray(j && j.groups) ? j.groups : []).map((g) => ({
    name: String((g && g.name) || '').trim(),
    items: (Array.isArray(g && g.items) ? g.items : Array.isArray(g && g.types) ? g.types : []).map((x) => String(x || '').trim()).filter(Boolean),
  })).filter((g) => g.name && g.items.length);
  const aliases = {};
  const a = (j && j.aliases) || {};
  Object.keys(a).forEach((k) => { if (a[k]) aliases[k] = String(a[k]); });
  return groups.length ? { groups, aliases } : null;
};

/** The list item a stored value is, or null ("X: words" counts as X; an old name through the aliases). */
export const itemOf = (vocab, raw) => {
  const s = String(raw || '').trim();
  if (!s || !vocab) return null;
  const all = [];
  vocab.groups.forEach((g) => g.items.forEach((i) => all.push(i)));
  const find = (v) => {
    const k = cargoKey(v);
    if (!k) return null;
    let hit = all.find((i) => cargoKey(i) === k) || all.find((i) => cargoKey(stripParen(i)) === k);
    if (hit) return hit;
    const al = vocab.aliases || {};
    const a = Object.keys(al).find((x) => cargoKey(x) === k);
    return a ? (all.find((i) => cargoKey(i) === cargoKey(al[a])) || null) : null;
  };
  const whole = find(s);
  if (whole) return whole;
  const i = s.indexOf(':');
  return i > 0 ? find(s.slice(0, i)) : null;
};

export const categoryOf = (vocab, raw) => {
  const it = itemOf(vocab, raw);
  if (!it) return null;
  const g = vocab.groups.find((x) => x.items.some((i) => cargoKey(i) === cargoKey(it)));
  return g ? g.name : null;
};

/** "Category › value" when it maps, else the value as stored. */
export const cargoLabel = (vocab, raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  const c = categoryOf(vocab, s);
  return c ? `${c} › ${s}` : s;
};

/** Dwell: an entry for the stored value; else for its type (under any of its names); else '@<category>'. null = none. */
export const cargoStopMinutes = (ct, commodity, vocab) => {
  if (!commodity || !ct || typeof ct !== 'object') return null;
  const n = (v) => { const f = parseFloat(v); return Number.isFinite(f) ? Math.round(f) : null; };
  const s = String(commodity).trim();
  if (Object.prototype.hasOwnProperty.call(ct, s) && n(ct[s]) != null) return n(ct[s]);
  if (!vocab) return null;
  const it = itemOf(vocab, s);
  if (!it) return null;
  for (const k of Object.keys(ct)) {
    if (k.charAt(0) === '@') continue;
    if ((cargoKey(k) === cargoKey(it) || itemOf(vocab, k) === it) && n(ct[k]) != null) return n(ct[k]);
  }
  const cat = categoryOf(vocab, it);
  if (cat) for (const k of Object.keys(ct)) if (k.charAt(0) === '@' && cargoKey(k.slice(1)) === cargoKey(cat) && n(ct[k]) != null) return n(ct[k]);
  return null;
};

const readSaved = () => {
  try { const v = cleanVocab(JSON.parse(window.localStorage.getItem(CACHE_KEY) || 'null')); return v; } catch (e) { return null; }
};

/** The list now (the last good copy), or null. */
export const cargoVocabNow = () => cur || (cur = readSaved());

/** Ask the hub (at most every 30 min unless forced); the last good copy when it cannot be reached. */
export const loadCargoVocab = async (force = false) => {
  if (!force && cur && cur.at && Date.now() - cur.at < TTL_MS) return cur;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const { data } = await supabase.auth.getSession();
      const t = (data && data.session && data.session.access_token) || '';
      if (!t) return cargoVocabNow();
      const r = await fetch(HUB + '/api/cargo-types', { headers: { Authorization: 'Bearer ' + t }, redirect: 'manual' });
      const j = r.status === 200 ? await r.json().catch(() => null) : null;
      const v = j && j.success ? cleanVocab(j) : null;
      if (v) {
        cur = { ...v, at: Date.now() };
        try { window.localStorage.setItem(CACHE_KEY, JSON.stringify(v)); } catch (e) { /* private mode */ }
        return cur;
      }
    } catch (e) { /* hub unreachable: keep the last copy */ }
    return cargoVocabNow();
  })().finally(() => { inflight = null; });
  return inflight;
};
